<template>
    <div class="pd-page">
        <!-- Header -->
        <div class="pd-header">
            <div class="pd-header-left">
                <router-link to="/payroll-history" class="pd-back-btn">
                    <i class="fas fa-arrow-left"></i>
                </router-link>
                <div class="pd-icon-wrap"><i class="fas fa-file-invoice-dollar"></i></div>
                <div>
                    <h2 class="pd-title">Payroll Details</h2>
                    <span class="pd-subtitle" v-if="company">{{ company.name }}</span>
                </div>
            </div>
            <div class="pd-header-right" v-if="payrollPaymentSlips.length">
                <router-link :to="`/payroll-salary-report/${$route.params.id}`" class="pd-report-btn">
                    <i class="fas fa-chart-bar"></i>
                    Salary Report
                </router-link>
                <button class="pd-export-btn pd-btn-csv" @click="exportToCSV" title="Export to CSV">
                    <i class="fas fa-file-csv"></i>
                    CSV
                </button>
                <button class="pd-export-btn pd-btn-pdf" @click="exportToPDF" title="Export to PDF">
                    <i class="fas fa-file-pdf"></i>
                    PDF
                </button>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="pd-loading"><div class="pd-spinner"></div><span>Loading...</span></div>

        <!-- Content -->
        <template v-else-if="payrollPayment">
            <!-- Summary Cards -->
            <div class="pd-summary">
                <div class="pd-sum-card">
                    <div class="pd-sum-icon pd-sum-blue"><i class="fas fa-building"></i></div>
                    <div class="pd-sum-info">
                        <span class="pd-sum-label">Company</span>
                        <span class="pd-sum-val">{{ company && company.name || '—' }}</span>
                    </div>
                </div>
                <div class="pd-sum-card">
                    <div class="pd-sum-icon pd-sum-purple"><i class="fas fa-calendar-alt"></i></div>
                    <div class="pd-sum-info">
                        <span class="pd-sum-label">Period</span>
                        <span class="pd-sum-val">{{ getMonthName(payrollPayment.month) }} {{ payrollPayment.year }}</span>
                    </div>
                </div>
                <div class="pd-sum-card">
                    <div class="pd-sum-icon pd-sum-teal"><i class="fas fa-calendar-check"></i></div>
                    <div class="pd-sum-info">
                        <span class="pd-sum-label">From</span>
                        <span class="pd-sum-val">{{ payrollPayment.startDate || '—' }}</span>
                    </div>
                </div>
                <div class="pd-sum-card">
                    <div class="pd-sum-icon pd-sum-amber"><i class="fas fa-calendar-times"></i></div>
                    <div class="pd-sum-info">
                        <span class="pd-sum-label">To</span>
                        <span class="pd-sum-val">{{ payrollPayment.endDate || '—' }}</span>
                    </div>
                </div>
                <div class="pd-sum-card" v-if="company && company.createdAt">
                    <div class="pd-sum-icon pd-sum-rose"><i class="fas fa-clock"></i></div>
                    <div class="pd-sum-info">
                        <span class="pd-sum-label">Generated</span>
                        <span class="pd-sum-val">{{ company.createdAt }}</span>
                    </div>
                </div>
            </div>

            <!-- Payslips Section -->
            <div class="pd-section-header">
                <div class="pd-section-left">
                    <i class="fas fa-receipt"></i>
                    <h3 class="pd-section-title">Payslips</h3>
                    <span class="pd-section-count" v-if="payrollPaymentSlips.length">{{ payrollPaymentSlips.length }}</span>
                </div>
            </div>

            <div v-if="!payrollPaymentSlips.length" class="pd-empty">
                <div class="pd-empty-icon"><i class="fas fa-receipt"></i></div>
                <h3>No payslips found</h3>
            </div>
            <div v-else class="pd-table-wrap">
                <table class="pd-table">
                    <thead>
                        <tr>
                            <th class="th-idx">#</th>
                            <th>Employee Name</th>
                            <th class="text-right">CTC</th>
                            <th class="text-center">Leaves</th>
                            <th class="text-center">OT Hours</th>
                            <th class="text-right">Earnings</th>
                            <th class="text-right">Deductions</th>
                            <th class="text-right">Net Salary</th>
                            <th class="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(slip, idx) in payrollPaymentSlips" :key="slip.id || idx">
                            <td class="td-idx">{{ idx + 1 }}</td>
                            <td class="td-name">
                                <span class="td-name-icon" :class="pdColor(idx)"><i class="fas fa-user-tie"></i></span>
                                {{ slip.employeeName }}
                            </td>
                            <td class="text-right">&#8377;{{ formatNum(slip.ctc) }}</td>
                            <td class="text-center">{{ slip.totalLeaves || 0 }}</td>
                            <td class="text-center">{{ round2(slip.totalOvertimeHours) || 0 }}</td>
                            <td class="text-right td-earn">&#8377;{{ formatNum(slip.totalEarningAmount) }}</td>
                            <td class="text-right td-ded">&#8377;{{ formatNum(slip.totalDeductionAmount) }}</td>
                            <td class="text-right td-net">&#8377;{{ formatNum(slip.netSalaryAmount) }}</td>
                            <td class="text-center">
                                <a class="pd-view-link" target="_blank"
                                    :href="`${serverURL}/api/salary-slips/${slip.name}`">
                                    <i class="fas fa-file-pdf"></i> View
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- No Data -->
        <div v-else class="pd-empty">
            <div class="pd-empty-icon"><i class="fas fa-file-invoice-dollar"></i></div>
            <h3>No payroll history found</h3>
        </div>
    </div>
</template>

<script>
import { getMonthName, round2 } from "../../utils/common"
import { http, serverURL } from "../../utils/http"

export default {
    data() {
        return {
            company: null,
            loading: false,
            payrollPayment: null,
            payrollPaymentSlips: [],
            serverURL,
        }
    },
    mounted() { this.fetchPayrollHistory() },
    methods: {
        getMonthName,
        round2,
        pdColor(i) { return ["pd-clr-blue","pd-clr-purple","pd-clr-teal","pd-clr-amber","pd-clr-rose"][i % 5] },
        formatNum(v) { if (v == null) return '0'; return Number(v).toLocaleString('en-IN') },
        async fetchPayrollHistory() {
            this.loading = true
            try {
                const res = await http.get(`/payroll-payments/${this.$route.params.id}`)
                const { company, payrollPayment, payrollPaymentSlips } = res.data.result
                this.company = company
                this.payrollPayment = payrollPayment
                this.payrollPaymentSlips = payrollPaymentSlips
            } catch (err) { console.log("error in fetching payroll history", err) }
            this.loading = false
        },
        exportToCSV() {
            const now = new Date()
            const generatedDateTime = now.toLocaleDateString('en-IN') + ' ' + now.toLocaleTimeString('en-IN')
            const period = this.payrollPayment ? `${this.getMonthName(this.payrollPayment.month)} ${this.payrollPayment.year}` : ''

            let csvContent = `Company Name,${this.company?.name || '—'}\n`
            csvContent += `Period,${period}\n`
            csvContent += `From Date,${this.payrollPayment?.startDate || '—'}\n`
            csvContent += `To Date,${this.payrollPayment?.endDate || '—'}\n`
            csvContent += `Generated Date & Time,${generatedDateTime}\n\n`

            const headers = ['#', 'Employee Name', 'CTC', 'Leaves', 'OT Hours', 'Earnings', 'Deductions', 'Net Salary']
            csvContent += headers.join(',') + '\n'

            this.payrollPaymentSlips.forEach((slip, idx) => {
                const row = [
                    idx + 1,
                    slip.employeeName,
                    slip.ctc,
                    slip.totalLeaves || 0,
                    this.round2(slip.totalOvertimeHours) || 0,
                    slip.totalEarningAmount,
                    slip.totalDeductionAmount,
                    slip.netSalaryAmount
                ]
                csvContent += row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',') + '\n'
            })

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            const fileName = `payroll-${this.company?.name || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`
            link.setAttribute('href', url)
            link.setAttribute('download', fileName)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        exportToPDF() {
            const now = new Date()
            const generatedDateTime = now.toLocaleDateString('en-IN') + ' ' + now.toLocaleTimeString('en-IN')
            const period = this.payrollPayment ? `${this.getMonthName(this.payrollPayment.month)} ${this.payrollPayment.year}` : ''

            const headers = ['#', 'Employee Name', 'CTC', 'Leaves', 'OT Hours', 'Earnings', 'Deductions', 'Net Salary']
            let tableHTML = '<table border="1" cellpadding="8" cellspacing="0" style="width:100%; border-collapse:collapse; font-family:Arial,sans-serif;">'
            tableHTML += '<thead style="background-color:#f0f0f0;"><tr>'
            headers.forEach(h => { tableHTML += `<th style="padding:10px; text-align:center; border:1px solid #ddd; font-weight:bold;">${h}</th>` })
            tableHTML += '</tr></thead><tbody>'
            this.payrollPaymentSlips.forEach((slip, idx) => {
                const bgColor = idx % 2 === 0 ? '#ffffff' : '#f9f9f9'
                tableHTML += `<tr style="background-color:${bgColor};"><td style="padding:8px; border:1px solid #ddd; text-align:center;">${idx + 1}</td>`
                tableHTML += `<td style="padding:8px; border:1px solid #ddd;">${slip.employeeName}</td>`
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(slip.ctc)}</td>`
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:center;">${slip.totalLeaves || 0}</td>`
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:center;">${this.round2(slip.totalOvertimeHours) || 0}</td>`
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(slip.totalEarningAmount)}</td>`
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(slip.totalDeductionAmount)}</td>`
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right; font-weight:bold;">₹${this.formatNum(slip.netSalaryAmount)}</td></tr>`
            })
            tableHTML += '</tbody></table>'

            let metadataHTML = '<div class="pdf-metadata" style="margin-bottom:20px; border:1px solid #e0e0e0; padding:15px; border-radius:4px; background-color:#fafafa;">'
            metadataHTML += '<table style="width:100%; border:none;">'
            metadataHTML += `<tr><td style="padding:4px; font-weight:bold; width:25%;">Company Name:</td><td style="padding:4px;">${this.company?.name || '—'}</td></tr>`
            metadataHTML += `<tr><td style="padding:4px; font-weight:bold;">Period:</td><td style="padding:4px;">${period}</td></tr>`
            metadataHTML += `<tr><td style="padding:4px; font-weight:bold;">From Date:</td><td style="padding:4px;">${this.payrollPayment?.startDate || '—'}</td></tr>`
            metadataHTML += `<tr><td style="padding:4px; font-weight:bold;">To Date:</td><td style="padding:4px;">${this.payrollPayment?.endDate || '—'}</td></tr>`
            metadataHTML += `<tr><td style="padding:4px; font-weight:bold;">Generated Date & Time:</td><td style="padding:4px;">${generatedDateTime}</td></tr>`
            metadataHTML += '</table></div>'

            const title = `Payroll Report - ${this.company?.name || 'Company'}`
            const printWindow = window.open('', '', 'height=600,width=900')
            printWindow.document.write(`<html><head><title>${title}</title><style>body{font-family:Arial,sans-serif; margin:20px;} h1{color:#333; text-align:center; font-size:18px; margin-bottom:5px;} .subtitle{text-align:center; color:#666; font-size:13px; margin-bottom:15px;} table{margin-top:20px;}</style></head><body>`)
            printWindow.document.write(`<h1>${this.company?.name || 'Payroll Report'}</h1>`)
            printWindow.document.write(`<div class="subtitle">Payroll Summary Report</div>`)
            printWindow.document.write(metadataHTML)
            printWindow.document.write(tableHTML)
            printWindow.document.write('</body></html>')
            printWindow.document.close()
            setTimeout(() => { printWindow.print() }, 250)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.pd-page { animation: pdFade .3s ease; }
@keyframes pdFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.pd-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.pd-header-left { display:flex; align-items:center; gap:10px; }
.pd-header-right { display:flex; align-items:center; gap:8px; }
.pd-back-btn { width:32px; height:32px; border-radius:8px; border:1px solid $gray200; display:flex; align-items:center; justify-content:center; color:$gray500; font-size:13px; transition:all $transitionFast; text-decoration:none; &:hover { background:$gray50; color:$primaryColor; border-color:$primaryColor; } }
.pd-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.pd-title { font-size:15px; font-weight:700; color:$gray800; margin:0; line-height:1.2; }
.pd-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }
.pd-report-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border:1px solid $gray200; border-radius:6px; background:white; font-size:12px; font-weight:600; cursor:pointer; transition:all $transitionFast; text-decoration:none; color:$primaryColor; i { font-size:10px; } &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.3); } }
.pd-export-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border:1px solid $gray200; border-radius:6px; background:white; font-size:12px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; i { font-size:10px; } }
.pd-btn-csv { color:#16a34a; &:hover { background:#f0fdf4; border-color:rgba(22,163,74,0.3); } }
.pd-btn-pdf { color:#dc2626; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.3); } }

/* Summary */
.pd-summary { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin-bottom:12px; }
.pd-sum-card { background:white; border:1px solid $gray100; border-radius:8px; padding:12px 14px; box-shadow:$shadowSm; display:flex; align-items:center; gap:10px; }
.pd-sum-icon { width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.pd-sum-blue { background:#eff6ff; color:#2563eb; }
.pd-sum-purple { background:#f5f3ff; color:#7c3aed; }
.pd-sum-teal { background:#f0fdfa; color:#0d9488; }
.pd-sum-amber { background:#fffbeb; color:#d97706; }
.pd-sum-rose { background:#fff1f2; color:#e11d48; }
.pd-sum-info { display:flex; flex-direction:column; min-width:0; }
.pd-sum-label { font-size:10px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.pd-sum-val { font-size:12.5px; color:$gray800; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* Section Header */
.pd-section-header { display:flex; align-items:center; justify-content:space-between; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; }
.pd-section-left { display:flex; align-items:center; gap:8px; i { font-size:13px; color:$primaryColor; } }
.pd-section-title { font-size:14px; font-weight:700; color:$gray800; margin:0; }
.pd-section-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }

/* Loading / Empty */
.pd-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.pd-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:pdSpin .7s linear infinite; }
@keyframes pdSpin { to { transform:rotate(360deg); } }
.pd-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.pd-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }

/* Payslips Table */
.pd-table-wrap { background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; overflow:hidden; }
.pd-table { width:100%; border-collapse:collapse; }
.pd-table thead { background:$gray50; border-bottom:2px solid $gray100; }
.pd-table th { padding:10px 14px; font-size:11px; font-weight:600; color:$gray500; text-transform:uppercase; letter-spacing:0.3px; white-space:nowrap; }
.pd-table td { padding:10px 14px; font-size:12.5px; color:$gray700; font-weight:500; border-bottom:1px solid $gray100; vertical-align:middle; white-space:nowrap; }
.pd-table tbody tr { transition:background $transitionFast; &:last-child td { border-bottom:none; } &:hover { background:$gray50; } }
.th-idx, .td-idx { width:36px; text-align:center; color:$gray400; font-size:11px; }
.td-name { display:flex; align-items:center; gap:8px; font-weight:600; color:$gray800; white-space:nowrap; }
.td-name-icon { width:28px; height:28px; border-radius:6px; display:inline-flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; }
.td-earn { color:#16a34a; font-weight:600; }
.td-ded { color:#dc2626; font-weight:600; }
.td-net { color:$primaryColor; font-weight:700; }
.text-right { text-align:right; }
.text-center { text-align:center; }
.pd-view-link { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; color:$primaryColor; text-decoration:none; transition:all $transitionFast; i { font-size:10px; } &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }

/* Color accents */
.pd-clr-blue { background:#eff6ff; color:#2563eb; }
.pd-clr-purple { background:#f5f3ff; color:#7c3aed; }
.pd-clr-teal { background:#f0fdfa; color:#0d9488; }
.pd-clr-amber { background:#fffbeb; color:#d97706; }
.pd-clr-rose { background:#fff1f2; color:#e11d48; }

/* Responsive */
@media(max-width:1200px) { .pd-summary { grid-template-columns:repeat(3,1fr); } .pd-table-wrap { overflow-x:auto; } }
@media(max-width:992px) { .pd-summary { grid-template-columns:repeat(2,1fr); } .pd-header { flex-direction:column; align-items:stretch; } .pd-header-right { justify-content:flex-end; } }
@media(max-width:480px) { .pd-summary { grid-template-columns:1fr; } .pd-header-right { flex-direction:column; } .pd-export-btn { width:100%; justify-content:center; } }
</style>
